import { useState } from 'react';
import { X, Bot, Truck, User, ArrowRight, Loader2, FileText, Plus, Upload, Image as ImageIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { useDialogA11y } from '@/hooks/useDialogA11y';

const UnifiedAssistantModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const dialogRef = useDialogA11y(true, onClose);
  const [activeTab, setActiveTab] = useState('customer'); // 'customer' or 'vehicle'
  const [inputMode, setInputMode] = useState('text'); // 'text' or 'image'
  const [inputText, setInputText] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: input, 2: preview, 3: choose action (vehicle only)
  const [dealNumber, setDealNumber] = useState('');
  const [attachingToDeal, setAttachingToDeal] = useState(false);

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        toast.error('Image size must be less than 10MB');
        return;
      }
      setSelectedImage(file);
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleExtract = async () => {
    if (inputMode === 'text') {
      if (!inputText.trim()) {
        toast.error('Please enter some text to extract information from');
        return;
      }
    } else {
      if (!selectedImage) {
        toast.error('Please select an image to extract information from');
        return;
      }
    }

    setLoading(true);
    try {
      if (inputMode === 'text') {
        // Text extraction (existing logic)
        const formData = new FormData();
        formData.append('text', inputText);

        const endpoint = activeTab === 'customer' ? '/ai-extract-customer' : '/ai-extract-vehicle';
        const response = await axios.post(`${API}${endpoint}`, formData);
        
        if (response.data.success) {
          setExtractedData(response.data.data);
          setStep(2);
          toast.success(`${activeTab === 'customer' ? 'Customer' : 'Vehicle'} information extracted successfully!`);
        } else {
          toast.error('Failed to extract information. Please try again.');
          console.error('Extraction error:', response.data);
        }
      } else {
        // Image extraction (new logic)
        const formData = new FormData();
        formData.append('file', selectedImage);

        const endpoint = activeTab === 'customer' ? '/ai-extract-customer-image' : '/ai-extract-vehicle-image';
        const response = await axios.post(`${API}${endpoint}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        if (response.data.success) {
          setExtractedData(response.data.data);
          setStep(2);
          toast.success(`✨ ${activeTab === 'customer' ? 'Customer' : 'Vehicle'} information extracted from image!`);
        } else {
          toast.error('Failed to extract information from image. Please try again.');
          console.error('Image extraction error:', response.data);
        }
      }
    } catch (error) {
      console.error('AI extraction error:', error);
      toast.error('Failed to extract information. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleNavigateToAddCustomer = () => {
    navigate('/customers/new', { state: { extractedData } });
    onClose();
  };

  const handleNewWorksheet = () => {
    navigate('/retail-worksheet/new', { state: { extractedVehicleData: extractedData } });
    onClose();
  };

  const handleAttachToDeal = async () => {
    if (!dealNumber.trim()) {
      toast.error('Please enter a deal number');
      return;
    }

    setAttachingToDeal(true);
    try {
      const dealsResponse = await axios.get(`${API}/deals`);
      const deal = dealsResponse.data.find(d => d.deal_number === dealNumber.trim());
      
      if (!deal) {
        toast.error(`Deal ${dealNumber} not found`);
        setAttachingToDeal(false);
        return;
      }

      navigate(`/retail-worksheet/${deal.id}`, { state: { extractedVehicleData: extractedData } });
      onClose();
      toast.success(`Vehicle information will be added to ${dealNumber}`);
    } catch (error) {
      console.error('Error finding deal:', error);
      toast.error('Failed to find deal. Please check the deal number.');
    } finally {
      setAttachingToDeal(false);
    }
  };

  const handleReset = () => {
    setInputText('');
    setSelectedImage(null);
    setImagePreview(null);
    setExtractedData(null);
    setDealNumber('');
    setStep(1);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setInputMode('text');
    setInputText('');
    setSelectedImage(null);
    setImagePreview(null);
    setExtractedData(null);
    setDealNumber('');
    setStep(1);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="AI Assistant"
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-gold-500 to-gold-600 p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Bot className="w-8 h-8" />
              <div>
                <h2 className="text-2xl font-bold">AI Assistant</h2>
                <p className="text-sm text-gold-100">Extract customer and vehicle information</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              aria-label="Close"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-2">
            <button
              onClick={() => handleTabChange('customer')}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
                activeTab === 'customer'
                  ? 'bg-white text-gold-600 shadow-lg'
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              <User className="w-5 h-5" />
              Customer Info
            </button>
            <button
              onClick={() => handleTabChange('vehicle')}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
                activeTab === 'vehicle'
                  ? 'bg-white text-gold-600 shadow-lg'
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              <Truck className="w-5 h-5" />
              Vehicle Info
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {step === 1 ? (
            // Step 1: Input Selection (Text or Image)
            <div className="space-y-4">
              {/* Input Mode Toggle */}
              <div className="flex gap-2 mb-4">
                <button
                  onClick={() => setInputMode('text')}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
                    inputMode === 'text'
                      ? 'bg-gold-600 text-white shadow-lg'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  <FileText className="w-5 h-5" />
                  Paste Text
                </button>
                <button
                  onClick={() => setInputMode('image')}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
                    inputMode === 'image'
                      ? 'bg-gold-600 text-white shadow-lg'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  <ImageIcon className="w-5 h-5" />
                  Upload Image
                </button>
              </div>

              {inputMode === 'text' ? (
                // Text Input Mode
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Paste {activeTab === 'customer' ? 'Customer' : 'Vehicle'} Information
                  </label>
                  <p className="text-sm text-slate-500 mb-3">
                    {activeTab === 'customer' 
                      ? 'Paste any text containing customer details (name, email, phone, address, employment info, etc.)'
                      : 'Paste any text containing vehicle details (year, make, model, VIN, color, mileage, price, etc.)'}
                  </p>
                  <textarea
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    placeholder={activeTab === 'customer' 
                      ? `Example:
John Smith
Email: john.smith@email.com
Phone: 416-555-0123
Address: 123 Main St, Toronto, ON M5V 1A1
DOB: 1985-03-15

Employer: ABC Corporation
Work Address: 456 Business Ave, Toronto, ON M4B 2C3
Income: $75,000 yearly`
                      : `Example:
2024 Toyota Camry LE
VIN: 4T1B11HK5RU123456
Color: Midnight Blue Metallic
Mileage: 15,000 km
Transmission: Automatic
Fuel Type: Gasoline
Condition: Used
Price: $28,999`}
                    className="w-full h-80 px-4 py-3 border-2 border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-gold-500 focus:border-transparent resize-none"
                  />
                </div>
              ) : (
                // Image Upload Mode
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Upload {activeTab === 'customer' ? 'Customer Document' : 'Vehicle Document'} Image
                  </label>
                  <p className="text-sm text-slate-500 mb-3">
                    {activeTab === 'customer' 
                      ? 'Upload an image of a driver\'s license, ID card, passport, or business card'
                      : 'Upload an image of vehicle registration, VIN plate, or vehicle listing'}
                  </p>
                  
                  {/* File Input */}
                  <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center hover:border-gold-400 transition-colors">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      className="hidden"
                      id="image-upload"
                    />
                    <label
                      htmlFor="image-upload"
                      className="cursor-pointer flex flex-col items-center gap-3"
                    >
                      {imagePreview ? (
                        <div className="space-y-3">
                          <img
                            src={imagePreview}
                            alt="Preview"
                            className="max-h-64 rounded-lg border-2 border-slate-200"
                          />
                          <p className="text-sm text-slate-600 font-medium">
                            {selectedImage?.name}
                          </p>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              setSelectedImage(null);
                              setImagePreview(null);
                            }}
                            className="px-4 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                          >
                            Remove Image
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="w-16 h-16 bg-gold-100 rounded-full flex items-center justify-center">
                            <Upload className="w-8 h-8 text-gold-600" />
                          </div>
                          <div>
                            <p className="text-lg font-semibold text-slate-700">
                              Click to upload or drag and drop
                            </p>
                            <p className="text-sm text-slate-500 mt-1">
                              Supports: JPG, PNG, HEIC, WebP (Max 10MB)
                            </p>
                          </div>
                        </>
                      )}
                    </label>
                  </div>
                </div>
              )}

              <div className="bg-gold-50 border-2 border-gold-200 rounded-xl p-4">
                <h3 className="font-semibold text-gold-900 mb-2">💡 Tips:</h3>
                <ul className="text-sm text-gold-700 space-y-1 list-disc list-inside">
                  {activeTab === 'customer' ? (
                    <>
                      <li>Include as much information as available</li>
                      <li>AI extracts: name, email, phone, address, DOB, employment details</li>
                      {inputMode === 'image' && (
                        <>
                          <li>Best for: Driver's licenses, ID cards, business cards</li>
                          <li>Ensure the image is clear and text is readable</li>
                        </>
                      )}
                      <li>You can review and edit extracted information before saving</li>
                    </>
                  ) : (
                    <>
                      <li>Include as much vehicle information as available</li>
                      <li>AI extracts: year, make, model, VIN, color, transmission, fuel type, mileage, price, condition</li>
                      {inputMode === 'image' && (
                        <>
                          <li>Best for: Registration documents, VIN plates, vehicle listings</li>
                          <li>Ensure the document is clear and text is readable</li>
                        </>
                      )}
                      <li>You can add to a new worksheet or attach to an existing deal</li>
                    </>
                  )}
                </ul>
              </div>
            </div>
          ) : (
            // Step 2: Preview & Actions
            <div className="space-y-4">
              <div className={`flex items-center gap-2 mb-4 ${activeTab === 'customer' ? 'text-green-600' : 'text-gold-600'}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${activeTab === 'customer' ? 'bg-green-100' : 'bg-gold-100'}`}>
                  {activeTab === 'customer' ? <User className="w-6 h-6" /> : <Truck className="w-6 h-6" />}
                </div>
                <div>
                  <h3 className="font-bold text-lg text-slate-800">
                    Extracted {activeTab === 'customer' ? 'Customer' : 'Vehicle'} Information
                  </h3>
                  <p className="text-sm text-slate-600">
                    {activeTab === 'customer' ? 'Review the details below before proceeding' : 'Choose how to use this information'}
                  </p>
                </div>
              </div>

              {/* Extracted Data Display */}
              <div className="bg-slate-50 border-2 border-slate-200 rounded-xl p-6 max-h-64 overflow-y-auto">
                <div className="grid grid-cols-2 gap-4">
                  {Object.entries(extractedData || {}).map(([key, value]) => (
                    <div key={key} className="bg-white p-3 rounded-lg border border-slate-200">
                      <p className="text-xs text-slate-500 font-medium uppercase mb-1">
                        {key.replace(/_/g, ' ').replace('vehicle ', '').replace('customer ', '')}
                      </p>
                      <p className="text-sm text-slate-800 font-semibold">{value || 'N/A'}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              {activeTab === 'customer' ? (
                <div className="bg-gold-50 border-2 border-gold-200 rounded-xl p-4">
                  <p className="text-sm text-gold-700">
                    ℹ️ You'll be able to edit all fields on the Add Customer page before saving.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <h4 className="font-semibold text-slate-700">What would you like to do?</h4>
                  
                  {/* New Worksheet Option */}
                  <button
                    onClick={handleNewWorksheet}
                    className="w-full p-4 border-2 border-gold-300 bg-gold-50 rounded-xl hover:bg-gold-100 transition-colors text-left group"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gold-500 rounded-lg flex items-center justify-center">
                          <Plus className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-800">Create New Worksheet</p>
                          <p className="text-sm text-slate-600">Start a new deal with this vehicle</p>
                        </div>
                      </div>
                      <ArrowRight className="w-5 h-5 text-gold-500 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </button>

                  {/* Attach to Existing Deal Option */}
                  <div className="p-4 border-2 border-green-300 bg-green-50 rounded-xl">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                        <FileText className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800">Attach to Existing Deal</p>
                        <p className="text-sm text-slate-600">Add vehicle info to a deal by number</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={dealNumber}
                        onChange={(e) => setDealNumber(e.target.value)}
                        placeholder="Enter deal number (e.g., DEAL-0001)"
                        className="flex-1 px-3 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                      <button
                        onClick={handleAttachToDeal}
                        disabled={attachingToDeal || !dealNumber.trim()}
                        className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {attachingToDeal ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Attaching...
                          </>
                        ) : (
                          <>
                            <ArrowRight className="w-4 h-4" />
                            Attach
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 p-6 bg-slate-50">
          <div className="flex justify-between gap-3">
            {step === 1 ? (
              <>
                <button
                  onClick={onClose}
                  className="px-6 py-3 border-2 border-slate-300 rounded-xl font-medium text-slate-700 hover:bg-slate-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleExtract}
                  disabled={loading || (inputMode === 'text' ? !inputText.trim() : !selectedImage)}
                  className="px-6 py-3 bg-gradient-to-r from-gold-500 to-gold-600 text-white rounded-xl font-medium hover:from-gold-600 hover:to-gold-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-lg"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Extracting...
                    </>
                  ) : (
                    <>
                      <Bot className="w-5 h-5" />
                      Extract Information
                    </>
                  )}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleReset}
                  className="px-6 py-3 border-2 border-slate-300 rounded-xl font-medium text-slate-700 hover:bg-slate-100 transition-colors"
                >
                  Start Over
                </button>
                {activeTab === 'customer' && (
                  <button
                    onClick={handleNavigateToAddCustomer}
                    className="px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-medium hover:from-green-600 hover:to-green-700 flex items-center gap-2 shadow-lg"
                  >
                    Continue to Add Customer
                    <ArrowRight className="w-5 h-5" />
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnifiedAssistantModal;
